/* Premium theme add-ons — composants légers, sans dépendance. */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Dates et nombres dans la langue affichée (fr, en…).
  const THEME_LOCALE = document.documentElement.lang || 'fr';

  /* ---------- Barre d'achat collante ---------- */
  if (!customElements.get('premium-sticky-atc')) {
    class PremiumStickyAtc extends HTMLElement {
      connectedCallback() {
        this.mainForm = document.querySelector('product-info product-form, product-form');
        this.mainButton = this.mainForm?.querySelector('[name="add"]');
        this.button = this.querySelector('[data-sticky-button]');
        if (!this.mainButton || !this.button) return;

        this.hidden = false;
        // Vérifié au scroll (et non via IntersectionObserver) pour gérer aussi les sauts de page.
        let ticking = false;
        const check = () => {
          ticking = false;
          const passed = this.mainButton.getBoundingClientRect().bottom < 0;
          this.classList.toggle('is-visible', passed);
          document.body.classList.toggle('has-sticky-atc', passed);
        };
        this.onScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(check);
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });
        check();

        this.button.addEventListener('click', () => {
          if (this.mainButton.disabled) {
            this.mainButton.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
            return;
          }
          this.mainButton.click();
        });

        // Synchronise prix / disponibilité avec la variante sélectionnée.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const html = event.data?.html;
            const variant = event.data?.variant;
            if (html) {
              const newPrice = html.querySelector('.price__container .price-item--sale, .price__container .price-item--regular');
              const priceTarget = this.querySelector('[data-sticky-price] span');
              if (newPrice && priceTarget) priceTarget.textContent = newPrice.textContent.trim();
            }
            if (variant) {
              this.button.disabled = !variant.available;
            }
          });
        }
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        this.unsubscribe?.();
        document.body.classList.remove('has-sticky-atc');
      }
    }
    customElements.define('premium-sticky-atc', PremiumStickyAtc);
  }

  /* ---------- Offre par quantité (Duo) ---------- */
  if (!customElements.get('premium-bundle')) {
    class PremiumBundle extends HTMLElement {
      connectedCallback() {
        this.inputs = [...this.querySelectorAll('input[type="radio"]')];
        this.inputs.forEach((input) => input.addEventListener('change', () => this.apply()));
        this.apply();
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const variant = event.data?.variant;
            if (variant) {
              this.dataset.price = variant.price;
              this.renderPrices();
            }
            // Dawn peut redessiner le champ quantité : on réapplique le choix.
            setTimeout(() => this.apply(), 0);
          });
        }
      }

      quantityField() {
        const id = this.dataset.formId;
        return (
          document.querySelector(`input[name="quantity"][form="${id}"]`) ||
          document.querySelector(`#${id} input[name="quantity"]`)
        );
      }

      apply() {
        const selected = this.inputs.find((input) => input.checked);
        if (!selected) return;
        this.inputs.forEach((input) => input.closest('label').classList.toggle('is-selected', input.checked));
        const field = this.quantityField();
        if (field && field.value !== selected.value) {
          field.value = selected.value;
          field.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Reprend le format de prix de la boutique (ex. « {{amount_with_comma_separator}} $ »).
      formatMoney(cents) {
        const template = this.dataset.moneyFormat;
        if (!template) {
          return new Intl.NumberFormat(THEME_LOCALE, { style: 'currency', currency: this.dataset.currency || 'CAD' }).format(cents / 100);
        }
        const group = (value, thousands, decimal, decimals = 2) => {
          const [int, dec] = (value / 100).toFixed(decimals).split('.');
          return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
        };
        return template.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
          switch (key) {
            case 'amount_with_comma_separator': return group(cents, '.', ',');
            case 'amount_no_decimals': return group(cents, ',', '', 0);
            case 'amount_no_decimals_with_comma_separator': return group(cents, '.', '', 0);
            case 'amount_with_space_separator': return group(cents, ' ', ',');
            case 'amount_with_apostrophe_separator': return group(cents, '\'', '.');
            default: return group(cents, ',', '.');
          }
        });
      }

      renderPrices() {
        const price = Number(this.dataset.price);
        if (!price) return;
        const format = { format: (value) => this.formatMoney(Math.round(value * 100)) };
        this.inputs.forEach((input) => {
          const qty = Number(input.value);
          const discount = Number(input.dataset.discount) || 0;
          const label = input.closest('label');
          const priceEl = label.querySelector('[data-bundle-price]');
          const compareEl = label.querySelector('[data-bundle-compare]');
          if (priceEl) priceEl.textContent = format.format((price * qty - Math.floor((price * discount) / 100) * qty) / 100);
          if (compareEl) compareEl.textContent = format.format((price * qty) / 100);
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }
    }
    customElements.define('premium-bundle', PremiumBundle);
  }

  /* ---------- Avant / Après ---------- */
  if (!customElements.get('premium-before-after')) {
    class PremiumBeforeAfter extends HTMLElement {
      connectedCallback() {
        const input = this.querySelector('input[type="range"]');
        if (!input) return;
        const update = () => this.style.setProperty('--position', `${input.value}%`);
        input.addEventListener('input', update);
        update();
      }
    }
    customElements.define('premium-before-after', PremiumBeforeAfter);
  }

  /* ---------- Lookbook : points interactifs ---------- */
  if (!customElements.get('premium-lookbook')) {
    class PremiumLookbook extends HTMLElement {
      connectedCallback() {
        this.hotspots = [...this.querySelectorAll('[data-hotspot]')];
        this.hotspots.forEach((hotspot) => {
          const trigger = hotspot.querySelector('[data-hotspot-trigger]');
          trigger?.addEventListener('click', (event) => {
            event.stopPropagation();
            const willOpen = !hotspot.classList.contains('is-open');
            this.closeAll();
            hotspot.classList.toggle('is-open', willOpen);
            trigger.setAttribute('aria-expanded', String(willOpen));
          });
        });
        this.onDocumentClick = (event) => {
          if (!event.target.closest('[data-hotspot]')) this.closeAll();
        };
        this.onKeyUp = (event) => {
          if (event.key === 'Escape') this.closeAll();
        };
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keyup', this.onKeyUp);
      }

      closeAll() {
        this.hotspots.forEach((hotspot) => {
          hotspot.classList.remove('is-open');
          hotspot.querySelector('[data-hotspot-trigger]')?.setAttribute('aria-expanded', 'false');
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keyup', this.onKeyUp);
      }
    }
    customElements.define('premium-lookbook', PremiumLookbook);
  }

  /* ---------- Compteurs animés ---------- */
  const animateCounter = (el) => {
    const target = parseFloat(el.dataset.countTo);
    if (Number.isNaN(target)) return;
    const decimals = (el.dataset.countTo.split('.')[1] || '').length;
    const format = (value) =>
      value.toLocaleString(THEME_LOCALE, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    if (reducedMotion) {
      el.textContent = format(target);
      return;
    }
    const duration = 1600;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = format(target * eased);
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const initCounters = (root = document) => {
    const counters = root.querySelectorAll('[data-count-to]:not([data-counted])');
    if (!counters.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.dataset.counted = 'true';
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );
    counters.forEach((counter) => observer.observe(counter));
  };

  /* ---------- Estimation de livraison (jours ouvrés) ---------- */
  const addBusinessDays = (date, days) => {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    return result;
  };

  const initDelivery = (root = document) => {
    root.querySelectorAll('[data-premium-delivery]').forEach((el) => {
      const now = new Date();
      const cutoff = parseInt(el.dataset.cutoff, 10);
      const start = new Date(now);
      if (now.getHours() >= cutoff || now.getDay() === 0 || now.getDay() === 6) {
        start.setDate(start.getDate() + 1);
      }
      const min = addBusinessDays(start, parseInt(el.dataset.minDays, 10));
      const max = addBusinessDays(start, parseInt(el.dataset.maxDays, 10));
      const formatter = new Intl.DateTimeFormat(THEME_LOCALE, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
      const target = el.querySelector('[data-delivery-range]');
      if (target) target.textContent = (target.dataset.template || 'entre le [min] et le [max]').replace('[min]', formatter.format(min)).replace('[max]', formatter.format(max));
    });
  };

  /* ---------- Hero produit : sélecteur de variante ---------- */
  const initHeroVariants = (root = document) => {
    root.querySelectorAll('[data-atelier-variant]').forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      const form = select.closest('form');
      const wrapper = select.closest('.atelier-ph__panel');
      const update = () => {
        const option = select.selectedOptions[0];
        if (!option || !wrapper) return;
        const current = wrapper.querySelector('[data-price-current]');
        const compare = wrapper.querySelector('[data-price-compare]');
        if (current) current.textContent = option.dataset.price;
        if (compare) {
          compare.textContent = option.dataset.compare || '';
          compare.hidden = !option.dataset.compare;
        }
        const button = form?.querySelector('[name="add"]');
        if (button) button.disabled = option.dataset.available !== 'true';
      };
      select.addEventListener('change', update);
    });
  };

  const init = (root) => {
    initCounters(root);
    initDelivery(root);
    initHeroVariants(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
